import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/material";
import _ from "lodash";
import { get } from "radash";
import React, { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, UseFormProps, UseFormReset, UseFormReturn, useForm } from "react-hook-form";
import { ZodError, ZodSchema, ZodTypeAny } from "zod";



import LoadingButton from "@components/LoadingButton";
import { LoadingButtonProps } from "@components/LoadingButton/LoadingButton";
import { DefaultFieldWrapper, FieldWrapperProps } from "@components/PatientForms/common/FormFieldWrapper";



import { devOnlyLog, scrollToFirstErrorField } from "@utils/helpers";



import FormFieldComponent from "./FormFieldComponent";
import { FormField } from "./types";





type AdditionalValidation = {
  name: string;
  rule: ZodSchema<any>;
};
/**
 * Props for the FormEngine component.
 */
export type FormEngineProps = {
  formId?: string;
  loading?: boolean;
  schema: FormField[]; // Array of form fields configuration
  cancelBtnTitle?: string;
  submitBtnTitle?: string;
  children?: React.ReactNode;
  data?: Record<string, any>; // Initial data for form fields
  validation?: ZodSchema<any>; // Zod schema for form validation
  onSubmit?: SubmitHandler<any>; // Submit handler function
  submitBtnProps?: Omit<LoadingButtonProps, "children">;
  cancelBtnProps?: Omit<LoadingButtonProps, "children">;
  onCancel?: (reset: UseFormReset<any>, data: any) => void; // Submit handler function
  format?: {
    fullWidthButtons?: boolean;
    buttonsDirection?: "row" | "column" | "row-reverse" | "column-reverse";
    spacing?: number;
  };
  formController?: UseFormReturn<any>; // useForm passed at parent level for more form control
  formOptions?: Partial<UseFormProps<any>>; // Additional options for useForm hook
  additionalValidation?: AdditionalValidation[]; // Allows validation for specific fields without the need of defining the base zodResolver validation
  render?: (form: UseFormReturn<any>, fields: JSX.Element) => React.ReactNode; // Optional render prop
  FieldWrapper?: React.FC<FieldWrapperProps>; // Optional render prop
};

/**
 * Trims all fields that have a string value in the form before submission.
 * @returns {Record<string, any>} The form values with trimmed string fields.
 */
const trimFormValues = (data: Record<string, any>) => {
  let values = { ...data };
  Object.keys(values).forEach((key) => {
    if (typeof values[key] === "string") {
      values[key] = values[key].trim();
    }
  });

  return values;
};

/**
 * FormEngine component renders a dynamic form based on provided schema and handles form submission.
 * @param data Initial data for form fields.
 * @param schema Array of form field configurations.
 * @param onSubmit Function to handle form submission.
 * @param render Optional render function for custom rendering.
 * @param validation Optional Zod schema for form validation.
 * @param formOptions Optional additional options for useForm hook.
 */
const FormEngine: React.FC<FormEngineProps> = ({
  formId,
  schema,
  render,
  loading,
  children,
  onCancel,
  data = {},
  validation,
  formOptions,
  formController,
  cancelBtnTitle,
  submitBtnTitle,
  submitBtnProps,
  cancelBtnProps,
  format = {
    fullWidthButtons: true,
    buttonsDirection: "column",
    spacing: 1,
  },
  onSubmit = () => {},
  additionalValidation = [],
  FieldWrapper = DefaultFieldWrapper,
}) => {
  // Initialize useForm hook with formData and optional validation
  const newForm = useForm({
    defaultValues: data,
    resolver: validation ? zodResolver(validation) : undefined,
    ...formOptions,
  });

  const form = formController || newForm;

  const {
    control,
    handleSubmit,
    getValues,
    clearErrors,
    setError,
    reset,
    formState: { isSubmitting },
  } = form;
  const { fullWidthButtons, buttonsDirection, spacing } = format;

  useEffect(() => {
    if (!_.isEmpty(data)) reset(data);
  }, [data]);

  // Form fields JSX
  const formFields = (
    <Stack spacing={spacing} my={1}>
      {schema.map((field, i) => (
        <FormFieldComponent field={field} control={control} FieldWrapper={FieldWrapper} />
      ))}
      {children}
    </Stack>
  );

  // Handle validation of fields if they exist. Allows validation based on conditional values as well
  const handleSubmissionValidation: FormEngineProps["onSubmit"] = (data, event) => {
    if (isSubmitting || loading) return; // Prevent multiple submissions

    let hasErrors = false;
    let errors: any = {};
    // Recursive function to validate a field and its group fields
    const validateField = (field: FormField, fieldConditional = {} as FormField["conditional"]) => {
      const validation: any = field?.rule as ZodTypeAny;
      const conditional = field?.conditional || fieldConditional;
      // Recursively validate fields within the group if they exist
      if (field.type === "group-input" && field.group) {
        field.group.forEach((groupField) => {
          validateField(groupField, groupField.conditional); // Recursive call
        });

        return;
      }

      let shouldValidate = true;
      if (validation && conditional?.values) {
        const watchedValue: any = get(data, conditional.label);
        shouldValidate = Array.isArray(watchedValue)
          ? watchedValue.some((value) => conditional.values.includes(value))
          : conditional.values.includes(watchedValue);
      }
      if (shouldValidate && validation) {
        try {
          // Perform Zod validation
          const fieldValue = get(data, field.name);
          validation.parse(fieldValue);
          clearErrors(field.name);
        } catch (error: unknown) {
          const err = error as ZodError;
          // Set has errors once only
          if (!hasErrors) {
            hasErrors = true;
          }

          // Scrolls to the first error on field
          scrollToFirstErrorField();

          const fieldError = {
            type: "manual",
            message: err.errors[0]?.message,
          };

          errors[field.name] = fieldError;
          setError(field.name, {
            type: "manual",
            message: err.errors[0]?.message,
          });
        }
      }
    };

    // Loop schema fields and validate each one
    schema.forEach((field) => {
      validateField(field);
    });

    // Validate additional fields
    additionalValidation.forEach((field) => {
      validateField(field as FormField);
    });

    if (!hasErrors) {
      onSubmit(trimFormValues(data), event);
    } else {
      devOnlyLog({ errors, values: data });
    }
  };

  // Render the form using render prop if provided, otherwise render formFields directly
  return (
    <FormProvider {...form}>
      <form id={formId} onSubmit={handleSubmit(handleSubmissionValidation)}>
        {render ? render(form, formFields) : formFields}
        {(submitBtnTitle || cancelBtnTitle) && (
          <Stack spacing={1} py={1.2} direction={buttonsDirection}>
            {submitBtnTitle && (
              <LoadingButton
                type="submit"
                loading={loading || isSubmitting}
                variant="contained"
                size="large"
                fullWidth={fullWidthButtons}
                disabled={loading || isSubmitting}
                sx={{ width: "fit-content", ...submitBtnProps?.sx }}
                {...submitBtnProps}
              >
                {submitBtnTitle || "Save"}
              </LoadingButton>
            )}
            {cancelBtnTitle && (
              <LoadingButton
                type="button"
                variant="outlined"
                fullWidth={fullWidthButtons}
                size="large"
                disabled={loading || isSubmitting || cancelBtnProps?.disabled}
                sx={{ width: "fit-content", ...cancelBtnProps?.sx }}
                {...cancelBtnProps}
                onClick={() => onCancel?.(reset, getValues())}
              >
                {cancelBtnTitle || "Cancel"}
              </LoadingButton>
            )}
          </Stack>
        )}
      </form>
    </FormProvider>
  );
};

export default FormEngine;